import type { GameView } from '../../../shared/api/game.ts';
import type { GameOverView } from '../../../shared/api/gameOver.ts';
import type { LobbyView } from '../../../shared/api/lobby.ts';
import type { PlayerChoosingView, PlayersGuessingView, RoundResultsView } from '../../../shared/api/round.ts';

export type GameModes = { playlist: boolean };

export type Page = { secondsLeft: string; showRules: boolean; modes: GameModes; resync: () => unknown };

type Timed<Member> = { data: Member; secondsLeft: string; isHost: boolean };

export type PickedView =
  | { name: 'ChooserChoosing'; props: Timed<PlayerChoosingView> }
  | { name: 'ChooserRoundResult'; props: Timed<RoundResultsView> }
  | { name: 'ChooserWaiting'; props: Timed<PlayersGuessingView> }
  | { name: 'GameAbandoned'; props: Record<string, never> }
  | { name: 'GameOver'; props: { view: GameOverView; onChanged: () => unknown } }
  | { name: 'GameStarting'; props: { secondsLeft: string } }
  | { name: 'GuesserGuessed'; props: Timed<PlayersGuessingView> }
  | { name: 'GuesserGuessing'; props: Timed<PlayersGuessingView> & { onGuessedSong: () => unknown } }
  | { name: 'GuesserRoundResult'; props: Timed<RoundResultsView> }
  | { name: 'GuesserWaiting'; props: Timed<PlayerChoosingView> }
  | { name: 'Loading'; props: Record<string, never> }
  | { name: 'LobbyHost'; props: { data: LobbyView; showRules: boolean; onSettingsUpdated: () => unknown } }
  | { name: 'LobbyPlayer'; props: { data: LobbyView; showRules: boolean } };

export type ViewName = PickedView['name'];

const isHostIn = (view: { hostPlayer: { id: number }; player: { id: number } }) =>
  view.hostPlayer.id === view.player.id;

const isChooserIn = (view: { player: { id: number }; round: { chooser?: { id: number } } }) =>
  view.round.chooser?.id === view.player.id;

export const viewFor = (view: GameView | undefined, { secondsLeft, showRules, resync }: Page): PickedView => {
  if (!view) return { name: 'Loading', props: {} };

  switch (view.phase) {
    case 'LOBBY':
      return isHostIn(view)
        ? { name: 'LobbyHost', props: { data: view, showRules, onSettingsUpdated: resync } }
        : { name: 'LobbyPlayer', props: { data: view, showRules } };
    case 'GAME_STARTING':
      return { name: 'GameStarting', props: { secondsLeft } };
    case 'PLAYER_CHOOSING':
      return isChooserIn(view)
        ? { name: 'ChooserChoosing', props: { data: view, secondsLeft, isHost: isHostIn(view) } }
        : { name: 'GuesserWaiting', props: { data: view, secondsLeft, isHost: isHostIn(view) } };
    case 'PLAYERS_GUESSING':
      if (isChooserIn(view))
        return { name: 'ChooserWaiting', props: { data: view, secondsLeft, isHost: isHostIn(view) } };
      return view.round.songSubmission
        ? { name: 'GuesserGuessed', props: { data: view, secondsLeft, isHost: isHostIn(view) } }
        : {
            name: 'GuesserGuessing',
            props: { data: view, secondsLeft, isHost: isHostIn(view), onGuessedSong: resync },
          };
    case 'ROUND_RESULTS':
      return isChooserIn(view)
        ? { name: 'ChooserRoundResult', props: { data: view, secondsLeft, isHost: isHostIn(view) } }
        : { name: 'GuesserRoundResult', props: { data: view, secondsLeft, isHost: isHostIn(view) } };
    case 'GAME_OVER':
      return { name: 'GameOver', props: { view, onChanged: resync } };
  }
};
